import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import {
  doCreateConversation,
  doDeleteConversation,
  doGetConversation,
  doReorderPinnedConversations,
  doSearchConversations,
  doUpdateConversation,
  luHandleAppiError,
  u3204BuildConversationSearchPayload,
  u3204MapConversationSearchRows,
  u3204NormalizeMissionRef,
  type U3204Conversation,
  type U3204ConversationSearchRowApi,
  type U3204DisplayMessage,
  type U3204ConversationSearchOptions,
} from '../U3204Service';
import {
  u3204HasPendingAssistant,
  u3204MergeClientMessageFields,
  u3204ResolveStreamStateAfterRefresh,
} from '../utils/u3204MessageClientState';
import { groupConversationsForSidebar } from '../utils/u3204GroupConversationsForSidebar';
import { u3204MergePinnedReorderIds } from '../utils/u3204PinnedReorder';

function useU3204ConversationApiErrorHandler() {
  const { t } = useTranslation(NS);
  const toast = useToast();

  return useCallback(
    (error: unknown, context?: string): boolean => {
      if (import.meta.env.DEV) {
        console.error(`[U3204] ${context ?? 'API error'}`, error);
      }

      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        Object.values(errorResult.fieldErrors).forEach((msg) => toast.error(String(msg)));
      }
      if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        Object.values(errorResult.otherErrors).forEach((msg) => toast.error(String(msg)));
      }
      return false;
    },
    [t, toast],
  );
}

export function useU3204ConversationHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const handleApiError = useU3204ConversationApiErrorHandler();
  const {
    initLU,
    activeConversationId,
    conversations,
    setConversations,
    setArchivedConversations,
    setLoadingConversations,
    setLoadingArchivedConversations,
    setLoadingMessages,
    setActiveConversationId,
    setActiveMission,
    setMessages,
    setStreamState,
    setIsNewChat,
    setActiveConversationTitle,
    selectedMissionId,
    conversationFilterText,
  } = useU3204Context();

  const buildSearchOptions = useCallback(
    (status: 'active' | 'archived', overrides?: Partial<U3204ConversationSearchOptions>): U3204ConversationSearchOptions => ({
      status,
      missionId: overrides?.missionId !== undefined ? overrides.missionId : selectedMissionId,
      searchText: overrides?.searchText !== undefined ? overrides.searchText : conversationFilterText,
    }),
    [conversationFilterText, selectedMissionId],
  );

  const loadConversations = useCallback(
    async (overrides?: Partial<U3204ConversationSearchOptions>) => {
      setLoadingConversations(true);
      try {
        const response = await doSearchConversations(
          axios,
          u3204BuildConversationSearchPayload(buildSearchOptions('active', overrides)),
        );
        const rawRows: U3204ConversationSearchRowApi[] = response.data?.results ?? [];
        const rows = u3204MapConversationSearchRows(rawRows);
        if (import.meta.env.DEV) {
          console.debug('[U3204] loadConversations', { count: rows.length, rawCount: rawRows.length });
        }
        setConversations(rows);
        return rows;
      } catch (error: unknown) {
      handleApiError(error, 'loadConversations');
      return [];
    } finally {
      setLoadingConversations(false);
    }
  }, [axios, buildSearchOptions, handleApiError, setConversations, setLoadingConversations]);

  const loadArchivedConversations = useCallback(
    async (overrides?: Partial<U3204ConversationSearchOptions>) => {
      setLoadingArchivedConversations(true);
      try {
        const response = await doSearchConversations(
          axios,
          u3204BuildConversationSearchPayload(buildSearchOptions('archived', overrides)),
        );
        const rawRows: U3204ConversationSearchRowApi[] = response.data?.results ?? [];
        const rows = u3204MapConversationSearchRows(rawRows);
        setArchivedConversations(rows);
        return rows;
      } catch (error: unknown) {
      handleApiError(error, 'loadArchivedConversations');
      return [];
    } finally {
      setLoadingArchivedConversations(false);
    }
  }, [axios, buildSearchOptions, handleApiError, setArchivedConversations, setLoadingArchivedConversations]);

  const refreshConversationLists = useCallback(async () => {
    await Promise.all([loadConversations(), loadArchivedConversations()]);
  }, [loadArchivedConversations, loadConversations]);

  const applyConversation = useCallback(
    (conversation: U3204Conversation, options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      setActiveConversationId(conversation.id);
      setActiveMission(u3204NormalizeMissionRef(conversation.mission));
      setActiveConversationTitle(conversation.title ?? null);
      setIsNewChat(false);
      const displayMessages: U3204DisplayMessage[] = (conversation.messages ?? []).map((m) => ({ ...m }));
      const hasPendingApi = u3204HasPendingAssistant(displayMessages);
      setMessages((prev) => {
        const merged = u3204MergeClientMessageFields(prev, displayMessages, { preserveLocalStream: silent });
        setStreamState(
          u3204ResolveStreamStateAfterRefresh({
            silent,
            hasPendingApi,
            hasPendingMerged: u3204HasPendingAssistant(merged),
          }),
        );
        return merged;
      });
    },
    [setActiveConversationId, setActiveMission, setActiveConversationTitle, setIsNewChat, setMessages, setStreamState],
  );

  const loadConversation = useCallback(
    async (conversationId: string, options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      if (!silent) {
        setLoadingMessages(true);
      }
      try {
        const response = await doGetConversation(axios, conversationId);
        const conversation: U3204Conversation = response.data?.result;
        if (conversation) {
          applyConversation(conversation, { silent });
          const targetPath = `/U3204/${conversationId}`;
          if (location.pathname !== targetPath) {
            navigate(targetPath, { replace: true });
          }
        }
        return conversation;
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      } finally {
        if (!silent) {
          setLoadingMessages(false);
        }
      }
    },
    [applyConversation, axios, handleApiError, location.pathname, navigate, setLoadingMessages],
  );

  const startNewChat = useCallback(() => {
    setActiveConversationId(null);
    setActiveMission(null);
    setMessages([]);
    setStreamState('idle');
    setActiveConversationTitle(null);
    setIsNewChat(true);
    navigate('/U3204', { replace: true });
  }, [navigate, setActiveConversationId, setActiveConversationTitle, setActiveMission, setIsNewChat, setMessages, setStreamState]);

  const createConversation = useCallback(
    async (missionId: string) => {
      try {
        const response = await doCreateConversation(axios, { missionId });
        const result = response.data?.result;
        if (!result?.id) return null;

        await refreshConversationLists();
        await loadConversation(result.id);
        return result;
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      }
    },
    [axios, handleApiError, loadConversation, refreshConversationLists],
  );

  const refreshActiveConversation = useCallback(async () => {
    if (!activeConversationId) return null;
    return loadConversation(activeConversationId, { silent: true });
  }, [activeConversationId, loadConversation]);

  const renameConversation = useCallback(
    async (id: string, version: number, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return false;

      try {
        await doUpdateConversation(axios, id, { id, version, title: trimmed });
        toast.success(t('renameSuccess', { ns: NS }));
        if (activeConversationId === id) {
          setActiveConversationTitle(trimmed);
        }
        await loadConversations();
        return true;
      } catch (error: unknown) {
        return handleApiError(error);
      }
    },
    [activeConversationId, axios, handleApiError, loadConversations, setActiveConversationTitle, t, toast],
  );

  const archiveConversation = useCallback(
    async (id: string, version: number) => {
      try {
        await doUpdateConversation(axios, id, { id, version, status: 'archived' });
        toast.success(t('archiveSuccess', { ns: NS }));
        if (activeConversationId === id) {
          startNewChat();
        }
        await refreshConversationLists();
        return true;
      } catch (error: unknown) {
        return handleApiError(error);
      }
    },
    [activeConversationId, axios, handleApiError, refreshConversationLists, startNewChat, t, toast],
  );

  const unarchiveConversation = useCallback(
    async (id: string, version: number) => {
      try {
        await doUpdateConversation(axios, id, { id, version, status: 'active' });
        toast.success(t('unarchiveSuccess', { ns: NS }));
        await refreshConversationLists();
        return true;
      } catch (error: unknown) {
        return handleApiError(error);
      }
    },
    [axios, handleApiError, refreshConversationLists, t, toast],
  );

  const deleteConversation = useCallback(
    async (id: string, version: number) => {
      try {
        await doDeleteConversation(axios, id, { id, version });
        toast.success(t('deleteSuccess', { ns: NS }));
        if (activeConversationId === id) {
          startNewChat();
        }
        await refreshConversationLists();
        return true;
      } catch (error: unknown) {
        return handleApiError(error);
      }
    },
    [activeConversationId, axios, handleApiError, refreshConversationLists, startNewChat, t, toast],
  );

  const setConversationPinned = useCallback(
    async (id: string, version: number, pinned: boolean) => {
      try {
        await doUpdateConversation(axios, id, { id, version, pinned });
        toast.success(t(pinned ? 'pinSuccess' : 'unpinSuccess', { ns: NS }));
        await loadConversations();
        return true;
      } catch (error: unknown) {
        return handleApiError(error);
      }
    },
    [axios, handleApiError, loadConversations, t, toast],
  );

  const pinConversation = useCallback(
    async (id: string, version: number) => setConversationPinned(id, version, true),
    [setConversationPinned],
  );

  const unpinConversation = useCallback(
    async (id: string, version: number) => setConversationPinned(id, version, false),
    [setConversationPinned],
  );

  const reorderPinnedConversations = useCallback(
    async (nextVisibleOrderedIds: string[]) => {
      if (nextVisibleOrderedIds.length === 0) {
        return false;
      }

      const filterActive = Boolean(selectedMissionId) || Boolean(conversationFilterText.trim());

      try {
        let fullOrderedIds: string[];

        if (filterActive) {
          const response = await doSearchConversations(
            axios,
            u3204BuildConversationSearchPayload({
              status: 'active',
              missionId: null,
              searchText: '',
            }),
          );
          const rawRows: U3204ConversationSearchRowApi[] = response.data?.results ?? [];
          const allRows = u3204MapConversationSearchRows(rawRows);
          fullOrderedIds =
            groupConversationsForSidebar(allRows)
              .find((group) => group.key === 'pinned')
              ?.conversations.map((c) => c.id) ?? [];
        } else {
          fullOrderedIds =
            groupConversationsForSidebar(conversations)
              .find((group) => group.key === 'pinned')
              ?.conversations.map((c) => c.id) ?? [];
        }

        if (fullOrderedIds.length === 0) {
          return false;
        }

        const payloadIds = u3204MergePinnedReorderIds(fullOrderedIds, nextVisibleOrderedIds);

        setConversations((prev) =>
          prev.map((row) => {
            if (!row.pinned) return row;
            const order = payloadIds.indexOf(row.id);
            if (order < 0) return row;
            return { ...row, pinnedOrder: order };
          }),
        );

        await doReorderPinnedConversations(axios, { conversationIds: payloadIds });
        toast.success(t('reorderPinnedSuccess', { ns: NS }));
        await loadConversations();
        return true;
      } catch (error: unknown) {
        await loadConversations();
        return handleApiError(error, 'reorderPinnedConversations');
      }
    },
    [
      axios,
      conversationFilterText,
      conversations,
      handleApiError,
      loadConversations,
      selectedMissionId,
      setConversations,
      t,
      toast,
    ],
  );

  return {
    initLU,
    conversations,
    loadConversations,
    loadArchivedConversations,
    refreshConversationLists,
    loadConversation,
    startNewChat,
    createConversation,
    applyConversation,
    refreshActiveConversation,
    renameConversation,
    archiveConversation,
    unarchiveConversation,
    deleteConversation,
    pinConversation,
    unpinConversation,
    reorderPinnedConversations,
  };
}
